class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      // 有些元素不想要自动提交表单，想要手动提交
      if (!event.target.hasAttribute("data-no-auto-submit")) {
        this.onSubmitHandler(event);
      }
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  // Submit finally
  onFinished() {
    document.dispatchEvent(new CustomEvent("swym:swym:init-action-buttons"));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false).finally(() => this.onFinished());
      this.onFinished();
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static async renderPage(searchParams, event, updateURLHash = true, useCache = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, .product-count--mb .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    for (const section of sections) {
      // 使用 let 以便在需要时修改 url
      let url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;

      if (document.body.classList.contains('template-search')) {
        // 如果有类名，在 URL 的最后添加 &type=product
        url += '&type=product';
      }

      const filterDataUrl = (element) => element.url === url;

      if (useCache) {
        FacetFiltersForm.filterData.some(filterDataUrl)
          ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
          : await FacetFiltersForm.renderSectionFromFetch(url, event);
      } else {
        await FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    }

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static async renderSectionFromFetch(url, event) {
    return fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    FacetFiltersForm.onFinished();
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, .product-count--mb .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    // document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    // 构造新的URL
    let newUrl = `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`;

    // 判断<body>元素是否有"template-search"类名
    if (document.body.classList.contains('template-search')) {
      // 如果有类名，在URL的最后添加 &type=product
      newUrl += '&type=product';
    }

    // 更新浏览器的历史记录
    history.pushState({ searchParams }, '', newUrl);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event, useCache = true) {
    FacetFiltersForm.renderPage(searchParams, event, true, useCache).finally(() => this.onFinished());
  }

  onSubmitHandler(event, useCache = true) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event, useCache);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event, useCache);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url).finally(() => this.onFinished());
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.rangeLowerBound = this.querySelector('.price-range__range-group input:first-child');
    this.rangeHigherBound = this.querySelector('.price-range__range-group input:last-child');
    this.textInputLowerBound = this.querySelector('[data-input-label-price-from]');
    this.textInputHigherBound = this.querySelector('[data-input-label-price-to]'); // Select whole text on focus for text field to improve user experience

    this.textInputLowerBound.addEventListener('keydown', this.onKeyDown.bind(this));
    this.textInputHigherBound.addEventListener('keydown', this.onKeyDown.bind(this));
    this.setMinAndMaxValues();

    this.textInputLowerBound.addEventListener('focus', () => {
      return this.textInputLowerBound.select();
    });
    this.textInputHigherBound.addEventListener('focus', () => {
      return this.textInputHigherBound.select();
    }); // Keep in sync the range with the text input fields

    this.textInputLowerBound.addEventListener('change', (event) => {
      const input = event.currentTarget;
      const value = Number(input.value);
      const min = Number(input.getAttribute('data-min') ?? input.min);
      const max = Number(input.getAttribute('data-max') ?? input.max);
      input.value = Math.max(Math.min(parseInt(value), parseInt(this.textInputHigherBound.value || max) - 1), min);
      this.rangeLowerBound.value = input.value;

      this.rangeLowerBound.parentElement.style.setProperty('--range-min', "".concat(parseInt(this.rangeLowerBound.value) / parseInt(this.rangeLowerBound.max) * 100, "%"));
    });
    this.textInputHigherBound.addEventListener('change', (event) => {
      const input = event.currentTarget;
      const value = Number(input.value);
      const min = Number(input.getAttribute('data-min') ?? input.min);
      const max = Number(input.getAttribute('data-max') ?? input.max);
      input.value = Math.min(Math.max(parseInt(value), parseInt(this.textInputLowerBound.value || min) + 1), max);
      this.rangeHigherBound.value = input.value;

      this.rangeHigherBound.parentElement.style.setProperty('--range-max', "".concat(parseInt(this.rangeHigherBound.value) / parseInt(this.rangeHigherBound.max) * 100, "%"));
    });

    this.rangeLowerBound.addEventListener('change', (event) => {
      this.textInputLowerBound.value = event.target.value;

      this.textInputLowerBound.dispatchEvent(new Event('change', {
        bubbles: true
      }));
    });
    this.rangeHigherBound.addEventListener('change', (event) => {
      this.textInputHigherBound.value = event.target.value;

      this.textInputHigherBound.dispatchEvent(new Event('change', {
        bubbles: true
      }));
    }); // We also have to bound the two range sliders

    this.rangeLowerBound.addEventListener('input', (event) => {
      event.target.value = Math.min(parseInt(event.target.value), parseInt(this.textInputHigherBound.value || event.target.max) - 1); // Bound the value

      event.target.parentElement.style.setProperty('--range-min', "".concat(parseInt(event.target.value) / parseInt(event.target.max) * 100, "%"));
      this.textInputLowerBound.value = event.target.value;
    });
    this.rangeHigherBound.addEventListener('input', (event) => {
      event.target.value = Math.max(parseInt(event.target.value), parseInt(this.textInputLowerBound.value || event.target.min) + 1); // Bound the value

      event.target.parentElement.style.setProperty('--range-max', "".concat(parseInt(event.target.value) / parseInt(event.target.max) * 100, "%"));
      this.textInputHigherBound.value = event.target.value;
    });
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const minInput = this.textInputLowerBound;
    const maxInput = this.textInputHigherBound;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 * 切换每页展示的产品数量
 */
class PerPageSelector extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector("custom-select");
    this.form = this.closest("facet-filters-form");

    this.select.addEventListener("change", (e) => {
      const value = e.detail.value;
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams["delete"]('page'); // We save the new value as a cart attribute, so it can be retrieved in Liquid easily :)
      currentUrl.searchParams["append"]('filter.my.per_page', "".concat(value));
      history.pushState('', '', currentUrl);

      fetch("".concat(window.routes.cart_url, "/update.js"), {
        body: JSON.stringify({
          attributes: {
            'collection_products_per_page': value
          }
        }),
        credentials: 'same-origin',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest' // This is needed as currently there is a bug in Shopify that assumes this header

        }
      }).then((res) => {
        this.form.onSubmitHandler(e, false);
      }).catch((err) => console.err(err));
      e.preventDefault();
    });
  }
}

customElements.define('per-page-selector', PerPageSelector);


/**
 * 原功能是开启后产品图片切换到第二张，后需要功能反向，因此变成关闭后产品图片切换到第二张;
 * 使用本地存储，默认为开启状态（显示第一张图）
 * 关闭后会在 body 添加类名：{pageType}-show-product-second-image
 */
class ProductSecondImageSwitch extends HTMLElement {
  // 页面类型，可为不同页面隔离状态，默认为 collection
  pageType;

  constructor() {
    super();
    this.pageType = this.getAttribute("data-page-type") ?? "collection";
    this.checkbox = this.querySelector("input");
    this.label = this.querySelector("span");
    // 本地存储对应 Key
    this.storageKey = `${this.pageType}-show-product-second-image`;

    // 读取本地存储状态
    const showSecond = "true" == (localStorage.getItem(this.storageKey) ?? "false");
    // checkbox 是反向的
    if (showSecond && this.checkbox.checked != false) {
      this.disable();
      this.checkbox.checked = false;
    }
    if (!showSecond && this.checkbox.checked != true) {
      this.checkbox.checked = true;
    }

    this._onChange = this.onChange.bind(this);
    this._onClick = this.onClick.bind(this);

    this.classList.remove("loading");
    this.checkbox.disabled = false;
  }

  connectedCallback() {
    this.checkbox.addEventListener("change", this._onChange);
    this.label.addEventListener("click", this._onClick);
  }

  disconnectedCallback() {
    this.checkbox.removeEventListener("change", this._onChange);
    this.label.removeEventListener("click", this._onClick);
  }

  onClick() {
    this.checkbox.click();
  }

  onChange(event) {
    if (this.checkbox.checked) {
      this.enable();
    } else {
      this.disable();
    }
  }

  enable() {
    localStorage.setItem(this.storageKey, "false");
    document.body.classList.remove(this.storageKey);
  }

  disable() {
    localStorage.setItem(this.storageKey, "true");
    document.body.classList.add(this.storageKey);
  }
}

customElements.define('product-second-image-switch', ProductSecondImageSwitch);


/**
 * 筛选侧边栏，移动端显示为 Drawer
 */
class FacetsVerticalDrawer extends HTMLElement {
  constructor() {
    super();
    this.querySelector(".facets-vertical-drawer__overlay")?.addEventListener("click", this.close.bind(this));
  }

  open() {
    this.classList.add("open");
  }

  close() {
    this.classList.remove("open");
  }
}

customElements.define('facets-vertical-drawer', FacetsVerticalDrawer);


/**
 * 筛选侧边栏 Drawer 的启动按钮
 */
class FacetsVerticalDrawerLauncher extends HTMLElement {
  constructor() {
    super();

    /** @type {FacetsVerticalDrawer} */
    this.drawer = document.querySelector("facets-vertical-drawer");

    this._onClick = this.onClick.bind(this);
  }

  onClick() {
    this.drawer.open();
  }

  connectedCallback() {
    this.addEventListener("click", this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
  }
}

customElements.define('facets-vertical-drawer-launcher', FacetsVerticalDrawerLauncher);


/**
 * 筛选侧边栏 Drawer 的关闭按钮
 */
class FacetsVerticalDrawerCloseButton extends HTMLElement {
  constructor() {
    super();

    /** @type {FacetsVerticalDrawer} */
    this.drawer = document.querySelector("facets-vertical-drawer");

    this._onClick = this.onClick.bind(this);
  }

  onClick() {
    this.drawer.close();
  }

  connectedCallback() {
    this.addEventListener("click", this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
  }
}

customElements.define('facets-vertical-drawer-close-button', FacetsVerticalDrawerCloseButton);

// 移动端悬浮筛选栏
(function () {
  const facetsHead = document.querySelector(".facets-horizontal-head");
  const onScroll = () => {
    if (facetsHead) {
      const top = facetsHead.getBoundingClientRect().top.toFixed(0);
      const headerHeight = (document.querySelector("sticky-header")?.getBoundingClientRect().height + 1) ?? 0;
      const isSticky = top > 0 && top <= headerHeight;
      if (isSticky && window.innerWidth < 990) {
        if (!facetsHead.classList.contains("is--sticky")) facetsHead.classList.add("is--sticky");
      } else {
        if (facetsHead.classList.contains("is--sticky")) facetsHead.classList.remove("is--sticky");
      }
    }
  };
  window.addEventListener("scroll", onScroll);
  onScroll();
})();